import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import './styles/darkmode.css';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthProvider } from './hooks/useAuth';
import HeaderShed from './components/HeaderShed';
import FooterShed from './components/FooterShed';
import ScrollToTop from './components/ScrollToTop';
import HomeShed from './pages/HomeShed';
import ShedInventory from './pages/ShedInventory';
import ShedDetail from './pages/ShedDetail';
import CartPage from './pages/CartPage';
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import FinanceApplication from './pages/FinanceApplication';
import NotFound from './pages/NotFound';
import PaymentSuccess from './pages/PaymentSuccess';
import CustomerPaymentPortal from './pages/CustomerPaymentPortal';
import TermsAndConditions from './pages/TermsAndConditions';
import Privacy from './pages/Privacy';
import Warranty from './pages/Warranty';
import RefundPolicy from './pages/RefundPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
import CityPage from './pages/CityPage';
import PasswordReset from './components/PasswordReset';
import DealerLogin from './pages/DealerLogin';
import DealerOnboard from './pages/DealerOnboard';
import ShedCheckout from './pages/ShedCheckout';
import ConfigureShed from './pages/ConfigureShed';
import DealerStripeCallback from './pages/DealerStripeCallback';
import './App.css';
import './styles/slick-vars.css';

function App() {
  // Handle Supabase auth redirects (password recovery, etc.)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    
    if (type === 'recovery' && accessToken) {
      window.location.href = '/reset-password' + window.location.hash;
    }
  }, []);

  // Initialize Preline UI
  useEffect(() => {
    // Wait for document to be fully loaded
    const observer = new MutationObserver(() => {
      if (document.readyState === 'complete') {
        if (window.HSStaticMethods) {
          window.HSStaticMethods.autoInit();
        }
        observer.disconnect();
      }
    });
    
    observer.observe(document, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
      <CartProvider>
      <WishlistProvider>
        <Router>
          <ScrollToTop />
          <div className="App min-h-screen bg-gray-50">
          <AnimatePresence mode="wait">
            <Routes>
              {/* Auth Routes */}
              <Route path="/reset-password" element={<PasswordReset />} />
              
              {/* Dealer Routes (no header/footer) */}
              <Route path="/dealer/login" element={<DealerLogin />} />
              <Route path="/dealer/onboard" element={<DealerOnboard />} />
              <Route path="/dealer/stripe-callback" element={<DealerStripeCallback />} />
              <Route path="/dealer/stripe-refresh" element={<DealerStripeCallback />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<Admin />} />
              <Route path="/ADMIN" element={<Admin />} />
              <Route path="/Admin" element={<Admin />} />
              
              {/* Public Routes with Header/Footer */}
              <Route
                path="/*"
                element={
                  <>
                    <HeaderShed />
                    <Routes>
                      {/* Home */}
                      <Route path="/" element={<HomeShed />} />
                      
                      {/* Shed Inventory */}
                      <Route path="/sheds" element={<ShedInventory />} />
                      <Route path="/inventory" element={<ShedInventory />} />
                      <Route path="/shed/:id" element={<ShedDetail />} />
                      
                      {/* Cart & Checkout */}
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/checkout" element={<CartPage />} />
                      <Route path="/buy/:shedId" element={<ShedCheckout />} />

                      {/* Custom Shed Configurator */}
                      <Route path="/configure" element={<ConfigureShed />} />
                      
                      {/* Payment */}
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/my-payments" element={<CustomerPaymentPortal />} />
                      <Route path="/payment-portal" element={<CustomerPaymentPortal />} />
                      
                      {/* Info Pages */}
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/warranty" element={<Warranty />} />
                      <Route path="/finance-application" element={<FinanceApplication />} />
                      <Route path="/sheds-for-sale/:citySlug" element={<CityPage />} />
                      
                      {/* Legal Pages */}
                      <Route path="/disclaimer" element={<Disclaimer />} />
                      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/refund-policy" element={<RefundPolicy />} />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      
                      {/* 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <FooterShed />
                  </>
                }
              />
            </Routes>
          </AnimatePresence>
          </div>
        </Router>
      </WishlistProvider>
      </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;